import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface OrgSearchResult {
  id: string;
  name: string;
  org_type: string;
  billing_email: string | null;
  created_at: string;
  property_count: number;
}

export interface OrgDetailUser {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  permission_role: string | null;
  approved: boolean;
  created_at: string;
}

export interface OrgDetailProperty {
  id: string;
  name: string;
  address: string | null;
  readiness_state: string | null;
  data_source_mode: string | null;
  onboarding_complete: boolean;
  created_at: string;
}

export interface OrgDetailPlan {
  id: string;
  name: string;
  price_cents: number;
  billing_interval: string;
  is_default: boolean;
  is_active: boolean;
}

export interface OrgDetailSubscription {
  id: string;
  property_name: string;
  plan_name: string;
  status: string;
  price_cents: number;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface OrgDetailIssue {
  id: string;
  title: string;
  severity: string;
  status: string;
  source: string;
  property_name: string;
  created_at: string;
}

export interface OrgDetailImport {
  id: string;
  provider_type: string;
  status: string;
  total_plants: number;
  processed_plants: number;
  created_at: string;
}

export interface OrgDetailData {
  org: {
    id: string;
    name: string;
    org_type: string;
    created_at: string;
    billing_email: string | null;
    billing_status: string | null;
    default_tier: string | null;
    stripe_connect_account_id: string | null;
    stripe_connect_charges_enabled: boolean;
    stripe_connect_payouts_enabled: boolean;
    default_customer_price_cents: number | null;
  };
  users: OrgDetailUser[];
  properties: OrgDetailProperty[];
  service_plans: OrgDetailPlan[];
  subscriptions: OrgDetailSubscription[];
  recent_issues: OrgDetailIssue[];
  recent_imports: OrgDetailImport[];
}

async function searchOrgs(query: string): Promise<OrgSearchResult[]> {
  const { data, error } = await supabase.rpc('admin_search_orgs', { p_query: query });
  if (error) throw error;
  return (data as OrgSearchResult[]) ?? [];
}

async function fetchOrgDetail(orgId: string): Promise<OrgDetailData> {
  const { data, error } = await supabase.rpc('admin_org_detail', { p_org_id: orgId });
  if (error) throw error;
  return data as OrgDetailData;
}

export function useAdminSearchOrgs(query: string) {
  return useQuery({
    queryKey: ['admin-search-orgs', query],
    queryFn: () => searchOrgs(query),
    enabled: query.trim().length >= 2,
  });
}

export function useAdminOrgDetail(orgId: string | null) {
  return useQuery({
    queryKey: ['admin-org-detail', orgId],
    queryFn: () => fetchOrgDetail(orgId!),
    enabled: !!orgId,
  });
}
